<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import KanbanAPI from 'dashboard/api/kanban';

const props = defineProps({
  card: { type: Object, required: true },
});

const emit = defineEmits(['cardUpdated']);

const { t } = useI18n();

const activities = ref([]);
const isLoading = ref(false);
const revertingId = ref(null);
const confirmingId = ref(null);

async function fetchActivities() {
  isLoading.value = true;
  try {
    const { data } = await KanbanAPI.listActivities(props.card.id);
    activities.value = data.data ?? [];
  } catch {
    activities.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function revertActivity(activityId) {
  revertingId.value = activityId;
  try {
    const { data } = await KanbanAPI.revertActivity(props.card.id, activityId);
    confirmingId.value = null;
    emit('cardUpdated', data.data);
    await fetchActivities();
  } catch (error) {
    const message =
      error?.response?.data?.error ??
      'Não foi possível desfazer esta alteração';
    useAlert(message);
  } finally {
    revertingId.value = null;
  }
}

function formatDate(timestamp) {
  const d = new Date(timestamp * 1000);
  const today = new Date();
  const isToday =
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();
  if (isToday) {
    return `Hoje às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const STATUS_LABELS = { open: 'Aberto', won: 'Ganho', lost: 'Perdido' };

function fallbackValue(value) {
  return STATUS_LABELS[value] ?? value ?? '—';
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = String(value);
  return div.innerHTML;
}

function bold(value) {
  return `<strong class="font-semibold text-n-slate-12">${escapeHtml(value)}</strong>`;
}

const NON_REVERTIBLE_EVENTS = ['card_created', 'card_deleted', 'card_restored'];

function activityConfig(activity) {
  const oldValue = bold(fallbackValue(activity.old_value));
  const newValue = bold(fallbackValue(activity.new_value));
  const configs = {
    card_created: {
      icon: 'i-lucide-plus-circle',
      color: 'text-n-teal-10',
      text: () => t('KANBAN.HISTORY.CREATED'),
    },
    card_deleted: {
      icon: 'i-lucide-trash-2',
      color: 'text-n-ruby-10',
      text: () => t('KANBAN.HISTORY.DELETED'),
    },
    card_restored: {
      icon: 'i-lucide-undo-2',
      color: 'text-n-teal-10',
      text: () => t('KANBAN.HISTORY.RESTORED'),
    },
    pipeline_changed: {
      icon: 'i-lucide-columns-3',
      color: 'text-n-blue-10',
      text: () =>
        t('KANBAN.HISTORY.PIPELINE_CHANGED', { old: oldValue, new: newValue }),
    },
    stage_changed: {
      icon: 'i-lucide-arrow-right-left',
      color: 'text-n-amber-10',
      text: () =>
        t('KANBAN.HISTORY.STAGE_CHANGED', { old: oldValue, new: newValue }),
    },
    status_changed: {
      icon: 'i-lucide-activity',
      color: 'text-n-ruby-10',
      text: () =>
        t('KANBAN.HISTORY.STATUS_CHANGED', { old: oldValue, new: newValue }),
    },
    assignee_changed: {
      icon: 'i-lucide-user-round',
      color: 'text-n-purple-10',
      text: () =>
        t('KANBAN.HISTORY.ASSIGNEE_CHANGED', { old: oldValue, new: newValue }),
    },
    title_changed: {
      icon: 'i-lucide-pencil',
      color: 'text-n-slate-10',
      text: () =>
        activity.old_value || activity.new_value
          ? t('KANBAN.HISTORY.TITLE_CHANGED', {
              old: bold(activity.old_value ?? '—'),
              new: bold(activity.new_value ?? '—'),
            })
          : t('KANBAN.HISTORY.TITLE_CHANGED_EMPTY'),
    },
    value_changed: {
      icon: 'i-lucide-banknote',
      color: 'text-n-teal-10',
      text: () =>
        t('KANBAN.HISTORY.VALUE_CHANGED', { old: oldValue, new: newValue }),
    },
    closing_date_changed: {
      icon: 'i-lucide-calendar',
      color: 'text-n-blue-10',
      text: () =>
        t('KANBAN.HISTORY.CLOSING_DATE_CHANGED', {
          old: oldValue,
          new: newValue,
        }),
    },
    source_changed: {
      icon: 'i-lucide-tag',
      color: 'text-n-amber-10',
      text: () =>
        t('KANBAN.HISTORY.SOURCE_CHANGED', { old: oldValue, new: newValue }),
    },
    custom_attribute_changed: {
      icon: 'i-lucide-sliders-horizontal',
      color: 'text-n-slate-10',
      text: () =>
        `Campo personalizado alterado de "${oldValue}" para "${newValue}"`,
    },
  };
  return configs[activity.event_type] ?? configs.card_created;
}

const newestActivities = computed(() => [...activities.value].reverse());

onMounted(fetchActivities);
</script>

<template>
  <div class="flex flex-col gap-4">
    <h4 class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider">
      {{ t('KANBAN.HISTORY.TITLE') }}
    </h4>

    <div v-if="isLoading" class="flex justify-center py-8">
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-9" />
    </div>

    <div v-else-if="newestActivities.length" class="flex flex-col gap-3">
      <div
        v-for="activity in newestActivities"
        :key="activity.id"
        class="group/act flex items-start gap-2.5"
      >
        <span
          class="size-4 mt-0.5 flex-shrink-0"
          :class="[
            activityConfig(activity).icon,
            activityConfig(activity).color,
          ]"
        />
        <div class="flex-1 min-w-0">
          <p
            class="text-sm text-n-slate-12"
            v-html="activityConfig(activity).text()"
          />
          <p class="text-xs text-n-slate-10 mt-0.5">
            {{ formatDate(activity.created_at) }}
            <span v-if="activity.user" class="text-n-slate-9">
              · {{ activity.user.name }}
            </span>
          </p>
        </div>

        <!-- Botão desfazer: não aparece para criação, exclusão e restauração -->
        <div
          v-if="!NON_REVERTIBLE_EVENTS.includes(activity.event_type)"
          class="flex-shrink-0 relative"
        >
          <!-- Popover de confirmação -->
          <div
            v-if="confirmingId === activity.id"
            class="absolute right-0 top-6 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl px-3 py-2 flex items-center gap-2 whitespace-nowrap"
            @click.stop
          >
            <span class="text-xs text-n-slate-11">{{
              t('KANBAN.HISTORY.CONFIRM_REVERT')
            }}</span>
            <button
              class="text-xs text-n-slate-10 hover:opacity-80 transition-opacity"
              :disabled="revertingId === activity.id"
              @click.stop="confirmingId = null"
            >
              {{ t('KANBAN.HISTORY.CANCEL') }}
            </button>
            <button
              class="text-xs text-n-blue-11 font-medium hover:opacity-80 transition-opacity disabled:opacity-40"
              :disabled="revertingId === activity.id"
              @click.stop="revertActivity(activity.id)"
            >
              <span
                v-if="revertingId === activity.id"
                class="i-lucide-loader size-3 animate-spin inline-block"
              />
              <span v-else>{{ t('KANBAN.HISTORY.CONFIRM') }}</span>
            </button>
          </div>

          <!-- Ícone de desfazer -->
          <button
            class="p-1 opacity-0 group-hover/act:opacity-60 hover:!opacity-100 transition-opacity"
            :title="t('KANBAN.HISTORY.REVERT')"
            @click.stop="
              confirmingId = confirmingId === activity.id ? null : activity.id
            "
          >
            <span class="i-lucide-rotate-ccw size-3.5 text-n-slate-10" />
          </button>
        </div>
      </div>
    </div>

    <p v-else class="text-xs text-n-slate-10 py-2">
      {{ t('KANBAN.HISTORY.EMPTY') }}
    </p>
  </div>
</template>
